"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useConnectAccountPoll } from "../hooks/useConnectAccountPoll";
import { useSocialProviderAccounts } from "../hooks/useSocialProviderAccounts";
import YouTubeReconnectDialog from "./YouTubeReconnectDialog";

/** Keep the publishing form mounted while OAuth runs in a separate window.
 * A successful migration returns to review; it never submits a stale batch. */
export default function YouTubeCapacityReconnect({
  accountIds,
  onReconnected,
  onCancel,
}: {
  accountIds: string[];
  onReconnected: () => void;
  onCancel: () => void;
}) {
  const { readAccounts: { data: accounts, refetch } } = useSocialProviderAccounts({ freshOnMount: true });
  const { connect, stopConnecting, isConnecting } = useConnectAccountPoll();
  const [attemptedAccountId, setAttemptedAccountId] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const starting = useRef(false);
  const mounted = useRef(true);
  const finished = useRef(false);
  // Retain ownership after navigation: Cancel must close the active OAuth
  // window even after the authorization request has completed.
  const pendingPopup = useRef<Window | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      pendingPopup.current?.close();
    };
  }, []);
  const remaining = useMemo(() => [...new Set(accountIds)].filter((id) => !accounts?.some(
    (account) => account.provider === "youtube" && account.id === id &&
      account.postbridgeConnected && !account.needsReconnect,
  )), [accountIds, accounts]);

  useEffect(() => {
    if (!attemptedAccountId || remaining.includes(attemptedAccountId) || finished.current) return;
    stopConnecting();
    pendingPopup.current?.close();
    pendingPopup.current = null;
    setAttemptedAccountId(null);
    if (remaining.length) {
      toast.success("YouTube channel reconnected. Reconnect the next channel to continue.");
    } else {
      finished.current = true;
      toast.success("YouTube reconnected. Review your posts to continue publishing.");
      onReconnected();
    }
  }, [attemptedAccountId, remaining, onReconnected, stopConnecting]);

  const reconnect = async () => {
    if (starting.current || isConnecting || finished.current) return;
    starting.current = true;
    setIsStarting(true);
    let launched = false;
    try {
      pendingPopup.current?.close();
      pendingPopup.current = null;
      // Open during the user's click, before either request loses the browser's
      // popup permission. Keep ownership until OAuth finishes or is dismissed.
      const popup = window.open("about:blank", "_blank");
      if (!popup) {
        toast.error("Allow pop-ups for Klap, then try reconnecting YouTube again.");
        return;
      }
      pendingPopup.current = popup;
      popup.document.title = "Connecting YouTube";
      popup.document.body.textContent = "Connecting to YouTube…";
      popup.document.body.style.cssText = "font-family: system-ui, sans-serif; padding: 24px";
      // A connection may have finished in another tab since the dialog opened.
      const { data, isError } = await refetch();
      if (!mounted.current || finished.current) return;
      if (isError || !data) {
        toast.error("Couldn't check your YouTube connection. Please try again.");
        return;
      }
      const accountId = [...new Set(accountIds)].find((id) => !data.some(
        (account) => account.provider === "youtube" && account.id === id &&
          account.postbridgeConnected && !account.needsReconnect,
      ));
      if (!accountId) {
        if (!finished.current) {
          finished.current = true;
          onReconnected();
        }
        return;
      }
      setAttemptedAccountId(accountId);
      connect("youtube", accountId, { via: "postbridge", popup });
      launched = true;
    } catch {
      if (mounted.current) toast.error("Couldn't check your YouTube connection. Please try again.");
    } finally {
      if (!launched) {
        pendingPopup.current?.close();
        pendingPopup.current = null;
      }
      starting.current = false;
      if (mounted.current) setIsStarting(false);
    }
  };

  const cancel = () => {
    finished.current = true;
    stopConnecting();
    pendingPopup.current?.close();
    pendingPopup.current = null;
    onCancel();
  };

  return <YouTubeReconnectDialog onReconnect={() => { void reconnect(); }} onCancel={cancel} isConnecting={isStarting || isConnecting} />;
}
